<template>
  <div class="sale-item-model-edit-form flex flex-col">
    <div class="primary-lighten-1 flex w-full flex-col gap-y-2 rounded-md p-2">
      <label>
        <span class="sr-only block text-sm">Artikelnaam</span>
        <InputText
          v-model="saleItem.name"
          ref="inputName"
          :errors="v$.name.$errors"
          @focusout="v$.name.$touch()"
        />
      </label>

      <label>
        <span class="sr-only block text-sm">Eenheid</span>
        <UnitCombobox
          v-model="saleItem.unit"
          input-placeholder="Eenheid"
          grow
          :errors="v$.unit.$errors"
          @focusout="v$.unit.$touch()"
        />
      </label>

      <ModelEditFormProjectSaleItem
        v-if="creationParameters?.project"
        :project="creationParameters.project"
        :sale-item="saleItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormProjectSaleItem"
      />

      <ModelEditFormKnownBusinessEntitySaleItem
        v-if="creationParameters.project"
        :project="creationParameters.project"
        :sale-item="saleItem"
        @input="anyPriceWasChanged = true"
        ref="modelEditFormKnownBusinessEntitySaleItem"
      />

      <label>
        <span class="block text-sm">Eenheidsprijs</span>
        <InputNumber
          v-model="saleItem.unitPrice"
          placeholder="Eenheidsprijs"
          allow-zero
          @input="anyPriceWasChanged = true"
        >
          <span class="sm:text-sm">&euro;</span>
        </InputNumber>
      </label>

      <label>
        <span class="block text-sm">Brandstoftoeslagfactor</span>
        <InputNumber
          v-model="saleItem.surchargeModifier"
          placeholder="Brandstoftoeslagfactor"
          allow-zero
        />
        <span class="block text-xs">Leeg = geen toeslag, 1 = onaangepaste toeslag</span>
      </label>

      <ModelEditFormRetroactivePricing
        v-if="!isCreating && anyPriceWasChanged"
        v-model="retroactiveDate"
        price-type="sale"
      >
        <template #subtitle>
          <span class="block text-xs">
            Alle verkopen <b>zonder productieverantwoording</b> vanaf deze datum worden aangepast
          </span>
        </template>
      </ModelEditFormRetroactivePricing>
    </div>
    <FormValidationAlert v-model="v$" />
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, ref } from "vue"
import InputText from "@/components/inputs/InputText.vue"
import InputNumber from "@/components/inputs/InputNumber.vue"
import { SaleItem } from "@/models/sale-item.model"
import { requiredValidator } from "@/helper/validation/validators"
import type { SaleItemController } from "@/controllers/model-controllers/sale-item-controller"
import { formFocusHack } from "@/helper/form-helper"
import { useFormConfigurator } from "@/composables/form/form-configurator"
import { upperFirst } from "lodash-es"
import UnitCombobox from "@/components/inputs/UnitCombobox.vue"
import { Project } from "@/models/project.model"
import ModelEditFormProjectSaleItem from "@/components/administrative_item/ModelEditFormProjectSaleItem.vue"
import ModelEditFormKnownBusinessEntitySaleItem from "@/components/administrative_item/ModelEditFormKnownBusinessEntitySaleItem.vue"
import ModelEditFormRetroactivePricing from "@/components/administrative_item/ModelEditFormRetroactivePricing.vue"
import SaleController from "@/controllers/model-controllers/sale-controller"
import { uniqueSaleItemValidator } from "@/helper/validation/sale-item-validation.ts"
import FormValidationAlert from "@/components/validation/FormValidationAlert.vue"

const inputName = ref()

const controller: SaleItemController = inject("saleItemController")!

const rules = {
  name: { requiredValidator },
  unit: { requiredValidator },
  $validator: uniqueSaleItemValidator
}

const {
  model: saleItem,
  initialText,
  isCreating,
  v$
} = useFormConfigurator<SaleItem>({
  controller,
  rules,
  customSaveHandler
})

const creationParameters: { project?: Project } = inject("creationParameters")!

onMounted(() => {
  if (initialText) {
    saleItem.value.name = upperFirst(initialText)
  }
  formFocusHack(inputName, saleItem, "name")
})

const saleController: SaleController = inject("saleController")!
const modelEditFormProjectSaleItem = ref()
const modelEditFormKnownBusinessEntitySaleItem = ref()
async function customSaveHandler(save: () => Promise<void>) {
  await save()
  if (creationParameters.project) {
    await Promise.all([
      modelEditFormProjectSaleItem.value.savePriceAgreement(),
      modelEditFormKnownBusinessEntitySaleItem.value.savePriceAgreement()
    ])
  }
  if (retroactiveDate.value) {
    await saleController.applyPricesRetroactively(saleItem.value, retroactiveDate.value)
  }
}

const anyPriceWasChanged = ref(false)
const retroactiveDate = ref()
</script>

<style scoped></style>
